// #profile's height, the same for every tab (FORMS.md § 9, as #register): Settings' natural
// height, measured each time the dialog opens on Settings (once its content has loaded), so it
// fits this user and this screen. Opened on another tab, it keeps the last height measured on
// this visit, else DEFAULT_HEIGHT (Settings with every section showing: 581px since its three
// sections, 2026-10-20; 567px from 2026-10-11, after the time zone left it). Capped at
// 95% of the screen: a taller tab, or a short screen (a phone held sideways), scrolls inside.

import {computed, nextTick, ref, watch} from "vue";

const DEFAULT_HEIGHT = 581; // px
const measuredHeight = ref(null);

const getNextPaint = () => new Promise((resolve) => {
  requestAnimationFrame(() => requestAnimationFrame(resolve));
});

function getNaturalHeight(card) {
  const fixed = card.style.height;
  card.style.height = "auto";
  const height = card.getBoundingClientRect().height;
  card.style.height = fixed;
  return Math.ceil(height);
}

// the dialog card's height with its own height unset (its max-height still applies); waits up to
// ~1 s for the card to be on screen (the dialog opens just after the hash changes)
async function fetchNaturalHeight(elementRef) {
  await nextTick();
  for (let frame = 0; frame < 60; frame++) {
    await getNextPaint();
    const card = elementRef.value?.closest(".content");
    const height = card?.getClientRects().length ? getNaturalHeight(card) : 0;
    if (height) return height;
  }
  return null;
}

// settingsRef: an element inside the Settings tab; isReady: its content has loaded
export function useSettingsHeight({isOpen, activeTab, isReady, settingsRef}) {
  let isMeasured = false; // once per opening
  watch([isOpen, activeTab, isReady], async ([open, tab, ready]) => {
    if (!open) return (isMeasured = false);
    if (isMeasured || tab !== "settings" || !ready) return;
    isMeasured = true;
    measuredHeight.value = (await fetchNaturalHeight(settingsRef)) || measuredHeight.value;
  }, {immediate: true});
  return computed(() => `min(${measuredHeight.value || DEFAULT_HEIGHT}px, 95dvh)`);
}
